// Resolução do módulo ativo a partir da rota. Puro e sem ícones de propósito —
// client/src/lib/modules.ts (que importa lucide-react) usa isto, e o teste cobre
// sem arrastar React pra dentro do vitest.

// "plataforma" é o módulo de quem administra o RIZOMA (users.plataforma_admin) —
// não é módulo de ORG e nunca aparece em `modules_enabled`. Ver `modulosDoUsuario`.
export type ModuleId = "cacho" | "equipe" | "manejo" | "mercado" | "plataforma";

// ⚠️ Fallback só para quando NÃO há módulo selecionado e a rota não diz nada. Numa
// org de COMPRADOR (que só tem 'mercado') quem decide a tela inicial é
// `visibleModules(...)[0]` em App.tsx, não esta constante — ela nunca deve virar
// "o módulo de todo mundo".
export const MODULO_PADRAO: ModuleId = "cacho";

// Telas de ADMINISTRAÇÃO — neutras em relação ao módulo. Abrir Conectores estando
// no Manejo não pode jogar o usuário no Cacho: administração é transversal, não
// pertence a módulo nenhum.
//
// ⚠️ A lista não é "tudo que começa com /admin". Metade das telas de
// administração mora sob /cacho por herança histórica (Variedades, Setores,
// Cores de Fitas, Motivos de Perda, Importar Planilha) — se fossem tratadas pelo
// prefixo, continuariam trocando o módulo pra Cacho, que é exatamente o bug.
// Ao mover ou acrescentar tela de administração, acrescentar aqui também.
export const ROTAS_ADMINISTRACAO: string[] = [
  "/admin",                 // Conectores, Usuários, Configurações, Assinatura
  "/agente",                // Rizoma Agent — plataforma, atravessa os 3 módulos
  "/cacho/variedades",
  "/cacho/setores",
  "/cacho/cores",
  "/cacho/motivos-perdas",
  "/cacho/importar",
];

export function ehRotaAdministracao(location: string): boolean {
  return ROTAS_ADMINISTRACAO.some(p => location === p || location.indexOf(p + "/") === 0);
}

const PREFIXO_POR_MODULO: Array<{ prefixo: string; id: ModuleId }> = [
  { prefixo: "/equipe", id: "equipe" },
  { prefixo: "/manejo", id: "manejo" },
  { prefixo: "/mercado", id: "mercado" },
  { prefixo: "/cacho", id: "cacho" },
  { prefixo: "/plataforma", id: "plataforma" },
];

/**
 * Qual módulo deve ficar selecionado nesta rota.
 *
 * - Rota de módulo (/cacho, /equipe, /manejo) → esse módulo. Entrar direto por
 *   URL continua selecionando o módulo certo.
 * - Rota de administração → MANTÉM o que já estava selecionado. É o fix: antes,
 *   qualquer coisa fora de /equipe e /manejo caía no fallback e virava Cacho.
 * - Qualquer outra coisa (raiz, rota desconhecida) → o que estava, se a org ainda
 *   enxerga aquele módulo; senão, o PRIMEIRO módulo visível.
 *
 * `visiveis` é opcional só para não quebrar quem não tem a lista à mão — mas quem
 * monta menu deve passá-la sempre. Ver o aviso dentro da função.
 */
export function resolverModuloId(
  location: string,
  moduloAtual?: ModuleId | null,
  visiveis?: ModuleId[],
): ModuleId {
  if (!ehRotaAdministracao(location)) {
    const achado = PREFIXO_POR_MODULO.find(p => location === p.prefixo || location.indexOf(p.prefixo + "/") === 0);
    if (achado) return achado.id;
  }
  // ⚠️ O FALLBACK É O PRIMEIRO MÓDULO VISÍVEL, não a constante. Este `?? MODULO_PADRAO`
  // sozinho foi o menu de Cacho inteiro na tela do corretor em produção (13/09/2026):
  // ele abriu /admin/usuarios, a rota é neutra, não havia módulo selecionado — e a
  // sidebar montou o Cacho para uma org que só tem 'mercado'. Nenhum daqueles itens
  // abria: a API recusa todos.
  //
  // O `moduloAtual` também passa pelo filtro. Ele vem do localStorage e sobrevive a
  // logout, a troca de conta e a um redirect errado — um "cacho" gravado ali continuaria
  // mandando na tela de quem não tem Cacho.
  if (!visiveis || visiveis.length === 0) return moduloAtual ?? MODULO_PADRAO;
  if (moduloAtual && visiveis.indexOf(moduloAtual) >= 0) return moduloAtual;
  return visiveis[0];
}

/**
 * O módulo que a rota EXIGE para abrir — ou `null` se ela não é de módulo nenhum.
 *
 * ⚠️ NÃO É `resolverModuloId`, e a diferença é o ponto. Aquela responde "o que fica
 * selecionado na sidebar", e por isso trata /admin/* e as telas de cadastro sob /cacho
 * como NEUTRAS — administração não troca o módulo de quem está navegando. Esta responde
 * "quem pode entrar", e aí `/cacho/variedades` é Cacho como qualquer outra: é a tabela
 * de variedades de banana, e um corretor não tem nenhuma.
 *
 * `/admin/*` e `/agente` continuam fora — são transversais de verdade, e cada um já
 * tem o próprio gate (papel de admin, dono da plataforma).
 */
export function moduloExigidoPelaRota(location: string): ModuleId | null {
  const achado = PREFIXO_POR_MODULO.find(p => location === p.prefixo || location.indexOf(p.prefixo + "/") === 0);
  return achado ? achado.id : null;
}

/**
 * A HOME de cada módulo. Mora aqui, e não só no registry com ícones, porque é ela
 * que decide para onde vai quem acabou de entrar — e essa decisão precisa ser
 * testável sem arrastar React para dentro do vitest. O registry
 * (`client/src/lib/modules.ts`) LÊ deste mapa, para que não existam duas verdades.
 */
export const HOME_POR_MODULO: Record<ModuleId, string> = {
  cacho: "/cacho/inicio",
  equipe: "/equipe",
  manejo: "/manejo/operacoes",
  mercado: "/mercado",
  plataforma: "/plataforma",
};

/**
 * Onde a pessoa entra depois do login — ou `null` quando a org não tem módulo algum.
 *
 * ⚠️ NUNCA "/cacho/inicio" cravado. Era isso que o `login.tsx` fazia, e é a origem do
 * print do Sávio: corretor conclui o cadastro pelo romaneio, faz login, e cai numa tela
 * de plantação. Pior que a tela errada é o efeito colateral — a rota de módulo GRAVA o
 * módulo no localStorage, então o "cacho" ficava carimbado na sessão dele.
 *
 * O último módulo usado só vale se ainda for visível: é o que faz o produtor voltar para
 * o Manejo onde parou sem deixar um valor velho mandar em quem não tem aquele módulo.
 */
export function rotaInicialDoUsuario(
  modulesEnabled: string[],
  ultimoModulo?: string | null,
): string | null {
  const visiveis = modulosVisiveis(modulesEnabled);
  if (visiveis.length === 0) return null;
  const ultimo = visiveis.find(id => id === ultimoModulo);
  return HOME_POR_MODULO[ultimo ?? visiveis[0]];
}

/** A rota define o módulo? Só nesse caso vale persistir a escolha. */
export function rotaDefineModulo(location: string): boolean {
  if (ehRotaAdministracao(location)) return false;
  return PREFIXO_POR_MODULO.some(p => location === p.prefixo || location.indexOf(p.prefixo + "/") === 0);
}

/**
 * Quais módulos uma org enxerga — 100% a partir de `modules_enabled`.
 *
 * ⚠️ "Cacho é sempre base" SAIU (Mercado C2). Enquanto toda org era produtora, cravar
 * o Cacho era inofensivo; com a org de COMPRADOR, que só tem 'mercado', aquilo
 * mostraria o menu inteiro do Cacho para quem não tem nem os dados nem a permissão —
 * e a API recusaria toda tela do menu. A migração 055 garante 'cacho' em toda org
 * PRODUTOR justamente para que esta função possa ser leitura pura da coluna: a
 * premissa fica garantida no banco, não presumida aqui.
 *
 * Vive em shared/ (e não junto do registry com ícones) pelo mesmo motivo do resto
 * deste arquivo: o teste cobre sem arrastar React para dentro do vitest.
 */
export function modulosVisiveis(modulesEnabled: string[]): ModuleId[] {
  // ⚠️ SEM "plataforma": ela não é módulo de org, e `modules_enabled` nunca a
  // carrega. Quem a acrescenta é `modulosDoUsuario`, pela flag do usuário.
  const ordem: ModuleId[] = ["cacho", "equipe", "manejo", "mercado"];
  return ordem.filter(id => modulesEnabled.indexOf(id) >= 0);
}

/**
 * Os módulos que a PESSOA vê no seletor: os efetivos dela mais a Plataforma,
 * quando ela administra o Rizoma (`users.plataforma_admin`).
 *
 * A Plataforma vai por ÚLTIMO e é OUTRO EIXO: não vem de `modules_enabled`, não
 * entra em `rotaInicialDoUsuario` (quem entra vai para a fazenda dele, não para
 * a administração da plataforma) e não muda nada para quem não tem a flag.
 */
export function modulosDoUsuario(modulosEfetivosDoUsuario: ModuleId[], plataformaAdmin: boolean): ModuleId[] {
  const semPlataforma: ModuleId[] = modulosEfetivosDoUsuario.filter(id => id !== "plataforma");
  return plataformaAdmin ? semPlataforma.concat(["plataforma"]) : semPlataforma;
}

/**
 * Os módulos que ESTE usuário vê: os da org ∩ os dele (migração 066).
 *
 * `modulosDoUsuario` NULL = todos os da org — é o estado de todo mundo antes da
 * coluna existir, e continua sendo o padrão de quem é cadastrado sem restrição.
 * A ordem é sempre a canônica (`modulosVisiveis`), nunca a da lista do usuário:
 * é ela que decide a home e o seletor, e duas ordens seriam duas verdades.
 *
 * ⚠️ Um usuário nunca ganha módulo que a org não tem — a interseção é a regra,
 * não a união. Restringir a `["equipe"]` numa org sem Equipe dá lista vazia, e
 * a raiz explica (`SemModulo`) em vez de abrir uma tela que a API recusa.
 */
export function modulosEfetivos(
  modulesEnabledDaOrg: string[],
  modulosDoUsuario: string[] | null | undefined,
): ModuleId[] {
  const daOrg = modulosVisiveis(modulesEnabledDaOrg);
  if (!modulosDoUsuario) return daOrg;
  return daOrg.filter(id => modulosDoUsuario.indexOf(id) >= 0);
}
